import { useMemo, useState } from "react";
import { Box } from "~/components/Box";
import { Container } from "~/components/Container";
import { DialogBody } from "~/components/Dialog";
import { dialogService } from "~/components/DialogStack";
import { Flex } from "~/components/Flex";
import { FormDialog } from "~/components/FormDialog";
import { SearchBar } from "~/components/Input";
import { DataColItem, DataListItem } from "~/components/List";
import { Text } from "~/components/Text";
import { H5 } from "~/components/Typography";
import { Approvers, Issuers } from "~/infrastructure/api/dtos";

export interface CustomerInfoDialogProps {
  approvers?: Approvers[];
  issuers?: Issuers[];
}

export function CustomerInfoDialog({ approvers = [], issuers = [] }: CustomerInfoDialogProps) {
  const [search, setSearch] = useState("");

  const filteredIssuers = useMemo(
    () =>
      issuers.filter((issuer) => {
        return (
          (issuer.name && issuer.name.toLowerCase().includes(search.toLowerCase())) ||
          (issuer.email && issuer.email.toLowerCase().includes(search.toLowerCase())) ||
          (issuer.phone && issuer.phone.toLowerCase().includes(search.toLowerCase())) ||
          (issuer.position && issuer.position.toLowerCase().includes(search.toLowerCase()))
        );
      }),
    [issuers, search],
  );

  const filteredApprovers = useMemo(
    () =>
      approvers.filter((approver) => {
        return (
          (approver.name && approver.name.toLowerCase().includes(search.toLowerCase())) ||
          (approver.email && approver.email.toLowerCase().includes(search.toLowerCase())) ||
          (approver.phone && approver.phone.toLowerCase().includes(search.toLowerCase())) ||
          (approver.position && approver.position.toLowerCase().includes(search.toLowerCase()))
        );
      }),
    [approvers, search],
  );

  return (
    <Container size="8" fixed>
      <FormDialog
        title="Dados sobre o cliente"
        onClickDismissButton={() => dialogService.popDialog()}
      >
        <DialogBody css={{ p: "$6", maxHeight: "70vh" }}>
          <Flex css={{ width: "100%", mb: "$6" }}>
            <SearchBar
              placeholder="Buscar cliente"
              value={search}
              onSearchChange={(search) => setSearch(search)}
              style={{ width: "100%" }}
            />
          </Flex>
          <Box>
            <Flex>
              <H5>Solicitantes</H5>
            </Flex>
            {filteredIssuers.length > 0 ? (
              <Box css={{ mt: "$10" }}>
                {filteredIssuers.map((issuer, index) => (
                  <DataListItem
                    key={`issuer-${index}`}
                    css={{ mb: "$5", width: "100%" }}
                    isActive={true}
                  >
                    <DataColItem headerTitle="Nome" data={issuer.name} />
                    <DataColItem
                      headerTitle="Email"
                      data={issuer.email}
                      css={{ wordBreak: "break-all" }}
                    />
                    <DataColItem headerTitle="Cargo" data={issuer.position} />
                    <DataColItem headerTitle="Telefone" data={issuer.phone} />
                  </DataListItem>
                ))}
              </Box>
            ) : (
              <Box css={{ py: "$10" }}>
                <Text>Não há solicitantes correspondentes</Text>
              </Box>
            )}
            <Flex>
              <H5>Aprovadores</H5>
            </Flex>
            {filteredApprovers.length > 0 ? (
              <Box css={{ mt: "$10" }}>
                {filteredApprovers.map((approver, index) => (
                  <DataListItem key={`approver-${index}`} css={{ mb: "$5" }} isActive={true}>
                    <DataColItem headerTitle="Nome" data={approver.name} />
                    <DataColItem
                      headerTitle="Email"
                      data={approver.email}
                      css={{ wordBreak: "break-all" }}
                    />
                    <DataColItem headerTitle="Cargo" data={approver.position} />
                    <DataColItem headerTitle="Telefone" data={approver.phone} />
                  </DataListItem>
                ))}
              </Box>
            ) : (
              <Box css={{ py: "$10" }}>
                <Text>Não há aprovadores correspondentes</Text>
              </Box>
            )}
          </Box>
        </DialogBody>
      </FormDialog>
    </Container>
  );
}

CustomerInfoDialog.displayName = "CustomerInfoDialog";
